import { withPushTimeout } from '../utils/pushRequests';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

const NativeSettings = registerPlugin('MyPiggyNotifications');
const native = Capacitor.isNativePlatform();
let current = null;
let pendingTap = null;
let readingTap = false;
let initialized = null;
let listeners = [];
let resolveApnsToken;
const apnsTokenReady = new Promise(resolve => { resolveApnsToken = resolve; });
let queue = Promise.resolve();
let epoch = 0;
const subscribers = new Set();
const emit = () => subscribers.forEach(fn => fn());
export const subscribePush = fn => { subscribers.add(fn); return () => subscribers.delete(fn); };
// Capacitor plugin proxies expose a dynamic `then`; never return one from an async loader.
// Use the proxy directly so Promise resolution cannot mistake it for a thenable.
const serial = fn => { const result = queue.then(fn, fn); queue = result.catch(() => {}); return result; };
export const installationId = () => {
  let id = localStorage.getItem('mypiggy-push-installation-v1');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('mypiggy-push-installation-v1', id); }
  return id;
};
export async function pushRpc(client, name, args) {
  const controller = new AbortController();
  return withPushTimeout(async () => {
    const { data, error } = await client.rpc(name, args).abortSignal(controller.signal);
    if (error) throw error;
    return data;
  }, 'MyPiggy could not finish the request. Check your connection and try again.', 12000, () => controller.abort());
}
export async function nativePushStatus() {
  if (!native) return { receive: 'web', enabled: false };
  const [permission, settings] = await Promise.all([
    withPushTimeout(async () => FirebaseMessaging.checkPermissions(), 'Unable to check notification permission. Please try again.', 8000),
    withPushTimeout(() => NativeSettings.status(), 'Unable to check device notification settings. Please try again.', 8000),
  ]);
  return { ...permission, ...settings };
}
export async function openNotificationSettings() { if (native) await NativeSettings.openSettings(); }
export async function enablePushOnDevice() {
  const status = await nativePushStatus();
  // iOS cannot show its permission prompt again after a denial. Android can
  // also have permission granted while the app's notifications are disabled.
  if (status.receive === 'denied' || (status.receive === 'granted' && !status.enabled)) {
    await openNotificationSettings();
    return { settingsOpened: true };
  }
  const result = await syncPush(true);
  if (!result) throw new Error('Device registration could not finish. Check your notification choices and sign-in, then try again.');
  return result;
}
export async function syncPush(requestPermission = false) {
  return serial(async () => {
    const ctx = current, ticket = epoch;
    if (!ctx || !native) return;
    const fcm = FirebaseMessaging;
    const prefs = await pushRpc(ctx.client, 'push_preferences', { p_audience: ctx.audience });
    // Never create a provider token before adult opt-in / Organizer consent.
    if (!prefs.consented || (!prefs.daily_tip && !prefs.weekly_reminder)) return;
    let status = await nativePushStatus();
    if (requestPermission && ['prompt','prompt-with-rationale'].includes(status.receive)) {
      await fcm.requestPermissions();
      status = await nativePushStatus();
    }
    if (ticket !== epoch || current !== ctx) return;
    const app = await App.getInfo();
    const key = `mypiggy-push-token:${app.id}`;
    let token = localStorage.getItem(key);
    if (status.receive === 'granted' && status.enabled) {
      // Permission and APNs registration complete independently. The native
      // plugin retains this event, including when Apple responds before bind.
      if (Capacitor.getPlatform() === 'ios') {
        await withPushTimeout(() => apnsTokenReady, 'Apple device registration took too long. Check your connection and tap Enable on this device to try again.', 20000);
        if (ticket !== epoch || current !== ctx) return;
      }
      token = (await withPushTimeout(() => fcm.getToken(), 'Device registration took too long. Check your connection and try again.', 20000)).token;
      localStorage.setItem(key, token);
    }
    if (!token || ticket !== epoch || current !== ctx) return;
    await pushRpc(ctx.client, 'push_register', {
      p_audience: ctx.audience, p_installation_id: installationId(), p_app_id: app.id,
      p_platform: Capacitor.getPlatform(), p_token: token,
      p_permission: status.enabled ? status.receive : 'denied',
      p_daily_channel: status.dailyChannel !== false, p_weekly_channel: status.weeklyChannel !== false,
    });
    emit();
    return { registered: status.receive === 'granted' && status.enabled };
  });
}
export async function detachPush(client, audience) {
  if (!native) return;
  const previous = current;
  ++epoch;
  current = null;
  // Fail closed: logout/account switching requires successful server detach.
  // This prevents offline logout from leaving a valid old-account registration.
  await serial(async () => {
    try {
      await pushRpc(client, 'push_unregister', { p_installation_id: installationId(), p_audience: audience });
    } catch (error) { current = previous; ++epoch; throw error; }
    const fcm = FirebaseMessaging;
    // The server binding is already revoked. Native cleanup is best effort.
    await fcm.removeAllDeliveredNotifications().catch(() => {});
    await fcm.deleteToken().catch(() => {});
    pendingTap = null;
  });
}
async function readTap() {
  if (readingTap) return;
  readingTap = true;
  try {
    // Keep the tap until a live handler displays it. Session/profile refreshes
    // can replace the runtime while the authorized message request is in flight.
    while (pendingTap && current) {
      const tap = pendingTap, ctx = current, ticket = epoch;
      if (ctx.isActive && !ctx.isActive()) return;
      try {
        const message = await pushRpc(ctx.client, 'push_read_message', {
          p_id: tap.attempt_id, p_binding: tap.binding_id, p_event: 'opened',
        });
        if (ctx !== current || ticket !== epoch || pendingTap !== tap) continue;
        if (ctx.isActive && !ctx.isActive()) return;
        pendingTap = null;
        if (message.audience === ctx.audience) ctx.onMessage(message);
      } catch {
        if (ctx !== current || ticket !== epoch || pendingTap !== tap) continue;
        if (ctx.isActive && !ctx.isActive()) return;
        pendingTap = null;
        ctx.onError('This notification is no longer available for this account.');
      }
    }
  } finally { readingTap = false; }
}
export function openPushNotification(data) { pendingTap = data; return readTap(); }
export async function bindPush(ctx) {
  current = ctx; ++epoch;
  if (!native) return () => { if (current === ctx) { current = null; ++epoch; } };
  if (!initialized) initialized = (async () => {
    const fcm = FirebaseMessaging;
    if (Capacitor.getPlatform() === 'android') {
      await fcm.createChannel({ id: 'mypiggy_daily_tips', name: 'Daily MyPiggy Tips', importance: 3, visibility: 0 });
      await fcm.createChannel({ id: 'mypiggy_weekly_reminders', name: 'Weekly Reminders', importance: 3, visibility: 0 });
    }
    listeners = await Promise.all([
      ...(Capacitor.getPlatform() === 'ios' ? [
        fcm.addListener('apnsTokenReceived', event => { if (event.token) resolveApnsToken(); }),
      ] : []),
      fcm.addListener('tokenReceived', () => { syncPush().catch(() => {}); }),
      fcm.addListener('notificationActionPerformed', event => {
        const d = event.notification?.data;
        if (!d || d.v !== '1' || !d.attempt_id || !d.binding_id) return;
        pendingTap = d; readTap();
      }),
      fcm.addListener('notificationReceived', async event => {
        const d = event.notification?.data, active = current;
        if (d?.v !== '1' || !active) return;
        try {
          const message = await pushRpc(active.client, 'push_read_message', { p_id: d.attempt_id, p_binding: d.binding_id, p_event: 'received' });
          if (active === current) active.onForeground?.(message, d);
        } catch { /* Wrong-account/stale payloads are ignored. */ }
      }),
    ]);
  })().catch(error => { initialized = null; throw error; });
  await initialized;
  if (ctx === current) {
    // Registration must not delay a tap or the runtime's cleanup handle.
    syncPush().catch(() => {});
    readTap();
  }
  return () => { if (current === ctx) { current = null; ++epoch; } };
}
// Retain listener handles for the lifetime of the bridge; never remove another plugin's listeners.
export const pushListenerCount = () => listeners.length;
